import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';

export type RankRequirementNatColumn = {
  id: number;
  label: string;
};

export type RankRequirementMatrixRow = {
  key: string;
  category: string;
  certificate: string;
  categoryOrder: number;
  certOrder: number;
  /** nationalityId → mandatory Yes */
  byNationality: Record<number, boolean>;
};

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function TableHead({ nationalities }: { nationalities: RankRequirementNatColumn[] }) {
  return (
    <table className="rr-print-table rr-print-table-head">
      <thead>
        <tr>
          <th className="rr-print-col-no">No.</th>
          <th className="rr-print-col-cat">Category</th>
          <th className="rr-print-col-cert">Certificate</th>
          {nationalities.map((n) => (
            <th key={n.id} className="rr-print-col-nat">
              {n.label}
            </th>
          ))}
        </tr>
      </thead>
    </table>
  );
}

function LineRow({
  row,
  index,
  nationalities,
}: {
  row: RankRequirementMatrixRow;
  index: number;
  nationalities: RankRequirementNatColumn[];
}) {
  return (
    <table className="rr-print-table rr-print-row-table">
      <tbody>
        <tr>
          <td className="rr-print-col-no">{index + 1}</td>
          <td className="rr-print-col-cat">{dash(row.category)}</td>
          <td className="rr-print-col-cert">{dash(row.certificate)}</td>
          {nationalities.map((n) => {
            const yes = !!row.byNationality[n.id];
            return (
              <td key={n.id} className={`rr-print-col-nat${yes ? ' rr-print-cell-yes' : ''}`}>
                {yes ? <span className="rr-print-pill rr-print-pill-yes">Yes</span> : '—'}
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

export type RankRequirementsReportProps = {
  rows: RankRequirementMatrixRow[];
  nationalities: RankRequirementNatColumn[];
  vesselName: string;
  rankName: string;
  rankAbrv?: string | null;
};

export const RankRequirementsReport: React.FC<RankRequirementsReportProps> = ({
  rows,
  nationalities,
  vesselName,
  rankName,
  rankAbrv = null,
}) => {
  const natLabels = nationalities.map((n) => n.label);

  const firstHeader = useMemo(
    () => (
      <div className="rr-print-flow-header">
        <div className="rr-print-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow title="Document Requirements" formId="By rank" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselName} />
              <ReportField
                label="Rank"
                value={rankAbrv ? `${rankName} (${rankAbrv})` : rankName}
              />
              <ReportField
                label="Nationalities"
                value={natLabels.length ? natLabels.join(', ') : '—'}
              />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>
        <TableHead nationalities={nationalities} />
      </div>
    ),
    [natLabels, nationalities, rankAbrv, rankName, vesselName],
  );

  const continueHeader = useMemo(
    () => (
      <div className="rr-print-flow-header">
        <div className="rr-print-continue-bar">
          <span className="rr-print-continue-title">Document Requirements</span>
          <span className="rr-print-continue-meta">
            {vesselName} · {rankAbrv || rankName}
          </span>
          <span className="rr-print-continue-hint">continued</span>
        </div>
        <TableHead nationalities={nationalities} />
      </div>
    ),
    [nationalities, rankAbrv, rankName, vesselName],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>
          Matrix of mandatory (Yes) requirements by nationality for this vessel and rank. “—” means
          not mandatory.
        </ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const colSpan = 3 + Math.max(nationalities.length, 1);

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="rr-print-table rr-print-row-table">
            <tbody>
              <tr>
                <td colSpan={colSpan} className="rr-print-empty">
                  No mandatory (Yes) requirements for this vessel and rank.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => (
        <LineRow key={row.key} row={row} index={i} nationalities={nationalities} />
      )),
      itemKeys: rows.map((row) => row.key),
    };
  }, [colSpan, nationalities, rows]);

  return (
    <ReportFlowPages
      className="rank-requirements-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default RankRequirementsReport;
